/**
 * Durable append-and-recover over IndexedDB — the "durable log now" decision made a
 * module. Records are persisted as they arrive (REQ-LOG-3), survive a tab kill, and are
 * listed for recovery on next load (REQ-LOG-4/7). `log.ts` builds the records; this only
 * stores and streams them, so the NDJSON schema stays testable without a storage mock.
 *
 * The `IDBFactory` is injected (REQ-NFR-4) so tests run against `fake-indexeddb` with no
 * globals. `IDBKeyRange` is used as an ambient constant, like `JSON` — it carries no state.
 */
import type { LogRecord } from './log';

/** What the recovery offer needs to describe an unfinished session. */
export type SessionSummary = {
  session: string;
  count: number;
  first?: string; // ISO `t` of the earliest record
  last?: string; // ISO `t` of the latest record
  build?: string; // from the session's meta record
};

export type Store = {
  /** Persist one record; resolves only once the write has committed (REQ-LOG-3). */
  append(session: string, rec: LogRecord): Promise<void>;
  /** Summaries of every stored session, for the recovery offer (REQ-LOG-4). */
  sessions(): Promise<SessionSummary[]>;
  /** Stream a session's records in `seq` order, paged so a long log need not fit in memory. */
  read(session: string): AsyncIterable<LogRecord>;
  /** Delete a session and its summary (REQ-UI-4). */
  remove(session: string): Promise<void>;
  close(): void;
};

const DB_VERSION = 1;
const RECORDS = 'records';
const SESSIONS = 'sessions';
const PAGE = 1000;

// The stored record wrapper: the compound key [session, seq] gives per-session range
// queries in seq order, and `rec` is the untouched LogRecord returned to callers.
type StoredRecord = { session: string; seq: number; rec: LogRecord };

// Internal summary; the public SessionSummary is projected from it in sessions().
type StoredSummary = {
  session: string;
  count: number;
  firstSeq?: number;
  firstT?: string;
  lastSeq?: number;
  lastT?: string;
  build?: string;
};

const promisify = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const txDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
  });

export function openStore(factory: IDBFactory, name = 'evbweb'): Promise<Store> {
  return new Promise((resolve, reject) => {
    const open = factory.open(name, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(RECORDS)) {
        db.createObjectStore(RECORDS, { keyPath: ['session', 'seq'] });
      }
      if (!db.objectStoreNames.contains(SESSIONS)) {
        db.createObjectStore(SESSIONS, { keyPath: 'session' });
      }
    };
    open.onerror = () => reject(open.error);
    open.onsuccess = () => resolve(makeStore(open.result));
  });
}

function makeStore(db: IDBDatabase): Store {
  async function append(session: string, rec: LogRecord): Promise<void> {
    const tx = db.transaction([RECORDS, SESSIONS], 'readwrite');
    const records = tx.objectStore(RECORDS);
    const sessions = tx.objectStore(SESSIONS);

    const stored: StoredRecord = { session, seq: rec.seq, rec };
    records.put(stored);

    const prev = (await promisify<StoredSummary | undefined>(sessions.get(session))) ?? {
      session,
      count: 0,
    };
    const next: StoredSummary = { ...prev, count: prev.count + 1 };
    if (next.firstSeq === undefined || rec.seq < next.firstSeq) {
      next.firstSeq = rec.seq;
      next.firstT = rec.t;
    }
    if (next.lastSeq === undefined || rec.seq > next.lastSeq) {
      next.lastSeq = rec.seq;
      next.lastT = rec.t;
    }
    if (rec.k === 'meta') next.build = rec.build;
    sessions.put(next);

    await txDone(tx);
  }

  async function sessions(): Promise<SessionSummary[]> {
    const tx = db.transaction(SESSIONS, 'readonly');
    const all = await promisify<StoredSummary[]>(tx.objectStore(SESSIONS).getAll());
    return all.map((s) => {
      const out: SessionSummary = { session: s.session, count: s.count };
      if (s.firstT !== undefined) out.first = s.firstT;
      if (s.lastT !== undefined) out.last = s.lastT;
      if (s.build !== undefined) out.build = s.build;
      return out;
    });
  }

  // Paged read: each page is its own transaction, so no cursor is held across an
  // await — which would let IndexedDB auto-commit the transaction out from under us.
  async function* read(session: string): AsyncIterable<LogRecord> {
    let from = 0;
    for (;;) {
      const tx = db.transaction(RECORDS, 'readonly');
      const range = IDBKeyRange.bound([session, from], [session, Number.MAX_SAFE_INTEGER]);
      const page = await promisify<StoredRecord[]>(tx.objectStore(RECORDS).getAll(range, PAGE));
      if (page.length === 0) return;
      for (const s of page) yield s.rec;
      const last = page[page.length - 1]!;
      from = last.seq + 1;
      if (page.length < PAGE) return;
    }
  }

  async function remove(session: string): Promise<void> {
    const tx = db.transaction([RECORDS, SESSIONS], 'readwrite');
    tx.objectStore(RECORDS).delete(
      IDBKeyRange.bound([session, 0], [session, Number.MAX_SAFE_INTEGER]),
    );
    tx.objectStore(SESSIONS).delete(session);
    await txDone(tx);
  }

  return { append, sessions, read, remove, close: () => db.close() };
}
